import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    IconButton,
    TextField,
    InputAdornment,
    Skeleton
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import "../css/Charts.css";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { dashboardService } from "../services/dashboardService";

const COLORS = ["#42a5f5", "#ffa726", "#66bb6a", "#66bbff", "#ff66aa"];

const JobGroupPieChart = () => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null); // Client, Status, Type
    const [categoryItems, setCategoryItems] = useState([]); // items in selected category
    const [selectedItem, setSelectedItem] = useState(null); // clicked item in category (string label)
    const [jobs, setJobs] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            const value = searchQuery.trim();

            if (value === "" || value.length > 2) {
                setDebouncedSearch(searchQuery);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        // Only fetch if an item is actually selected
        if (selectedItem && selectedCategory) {
            const fetchJobs = async () => {
                setLoadingJobs(true);
                try {
                    // Use the debounced value here
                    const jobsData = await dashboardService.getJobsByGroupForDashboard(
                        selectedCategory.toLowerCase(),
                        selectedItem,
                        debouncedSearch
                    );
                    setJobs(jobsData);
                } catch (err) {
                    console.error("Failed to fetch jobs by group item", err);
                } finally {
                    setLoadingJobs(false);
                }
            };
            fetchJobs();
        }
    }, [selectedItem, selectedCategory, debouncedSearch]);

    useEffect(() => {
        const fetchCounts = async () => {
            try {
                setLoading(true);
                const data = await dashboardService.getJobGroupCountsForDashboard();

                const formatted = Object.entries(data).map(([key, value], idx) => ({
                    id: idx,
                    label: key,
                    value: Object.keys(value).length,
                    color: COLORS[idx % COLORS.length],
                    rawData: value,
                }));

                setChartData(formatted);
            } catch (err) {
                console.error("Failed to fetch dashboard group counts", err);
            } finally {
                setLoading(false);
            }
        };
        fetchCounts();
    }, []);

    // const formatDate = (dateString) => {
    //     const date = new Date(dateString);
    //     const day = String(date.getDate()).padStart(2, '0');
    //     const month = String(date.getMonth() + 1).padStart(2, '0');
    //     const year = date.getFullYear();
    //     return `${day}/${month}/${year}`;
    // };

    const formatDate = (dateString) => {
        const [datePart] = dateString.split(' ');
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    };

    const formatISODate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "—";

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const showStatusUpdatedDate = selectedCategory === "Status" &&
        ["IR Query", "Query", "IP Query"].includes(selectedItem);

    // first-level slice click
    const handleCategoryClick = (event, pieItemIdentifier, item) => {
        if (!item) return;
        setSelectedCategory(item.label);
        const itemsArray = Object.entries(item.rawData).map(([label, count], idx) => ({
            id: idx,
            label,
            count,
        }));
        setCategoryItems(itemsArray);
        setSelectedItem(null); // reset any previously opened item dialog
    };

    // second-level item click -> open child dialog
    const handleItemClick = async (item) => {
        if (!item || !selectedCategory) return;

        setSearchQuery("");
        setDebouncedSearch("");
        setSelectedItem(item.label);
    };

    return (
        <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box className="chart-container">
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            gap: 2,
                        }}
                    >
                        <Skeleton variant="circular" width={170} height={170} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="42%" />
                    </Box>
                ) : (
                    <Box className="chart-pie-box">
                        <PieChart
                            series={[
                                {
                                    data: chartData,
                                    arcLabel: (arc) => {
                                        // Calculate percentage of this slice
                                        const total = chartData.reduce((sum, item) => sum + item.value, 0);
                                        const percentage = arc.value / total;
                                        return percentage > 0.05 ? `${arc.value}` : "";
                                    },
                                    arcLabelRadius: "65%",
                                    highlightScope: { faded: "none", highlighted: "none" },
                                },
                            ]}
                            margin={{ top: 10, bottom: 40, left: 30, right: 30 }}
                            slotProps={{
                                legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" }, padding: 0 },
                            }}
                            tooltip={{ trigger: "item" }}
                            className="pie-chart"
                            onItemClick={handleCategoryClick}
                        />
                    </Box>
                )}
            </Box>

            {/* PARENT: Drill-down modal for category items */}
            <Dialog
                open={Boolean(selectedCategory)}
                onClose={() => {
                    setSelectedCategory(null);
                    setCategoryItems([]);
                    setSelectedItem(null);
                    setJobs([]);
                }}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        className: "chart-dialog-paper",
                    },
                }}
                keepMounted
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
                        {selectedCategory !== null ? selectedCategory : "Jobs"}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setSelectedCategory(null);
                            setSelectedItem(null);
                            setJobs([]);
                        }}
                        size="small"
                        sx={{ position: "absolute", right: 12, top: 12 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">
                    <Box className="category-container">
                        {categoryItems.map((it) => (
                            <Box
                                key={it.id}
                                onClick={() => handleItemClick(it)}
                                className="category-item"
                            >
                                {it.label} ({it.count})
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* CHILD: Separate dialog that opens above the parent */}
            <Dialog
                open={Boolean(selectedItem)}
                onClose={() => setSelectedItem(null)}
                maxWidth="md"
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
                slotProps={{
                    paper: {
                        className: "chart-dialog-paper-two",
                    },
                }}
                keepMounted
                disableEnforceFocus
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
                        {selectedItem} Jobs
                    </Typography>
                    <IconButton aria-label="close" onClick={() => setSelectedItem(null)} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">

                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, }}>
                        <TextField
                            size="small"
                            placeholder="Type to search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                width: 300,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#f9fafb",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: "#f1f5f9",
                                    },
                                    "&.Mui-focused": {
                                        backgroundColor: "#fff",
                                        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                                    },
                                },
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    {loadingJobs ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : jobs.length === 0 ? (
                        <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>No jobs found for this item.</Typography>
                    ) : (
                        <TableContainer sx={{ maxHeight: "75vh" }}>
                            <Table stickyHeader size="small" className="chart-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Job Name</b></TableCell>
                                        {selectedCategory !== "Clients" && (<TableCell><b>Client Name</b></TableCell>)}
                                        {selectedCategory !== "Types" && (<TableCell><b>Job Type</b></TableCell>)}
                                        {selectedCategory !== "Status" && (<TableCell><b>Status</b></TableCell>)}
                                        {showStatusUpdatedDate && (<TableCell><b>Status Updated</b></TableCell>)}
                                        <TableCell><b>End Date</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobs.map((job, index) => (
                                        <TableRow key={job.id || index} hover>
                                            <TableCell className="cell-overflow">{job.name}</TableCell>
                                            {selectedCategory !== "Clients" && <TableCell className="cell-overflow">{job.client?.name}</TableCell>}
                                            {selectedCategory !== "Types" && <TableCell className="cell-overflow">{job.type}</TableCell>}
                                            {selectedCategory !== "Status" && <TableCell className="cell-overflow">{job.status}</TableCell>}
                                            {showStatusUpdatedDate && (
                                                <TableCell className="cell-overflow">{formatISODate(job.statusUpdatedDate)}</TableCell>
                                            )}
                                            <TableCell className="cell-overflow">{job.expectedEndDate ? formatDate(job.expectedEndDate) : "—"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default JobGroupPieChart;